import { useState } from 'react';
import { format } from 'date-fns';
import {
  ButtonPrimary,
  ButtonPseudo,
  ButtonSize,
  Checkbox,
  ClickOutsideDetector,
  IconDown,
  IconFilter,
  IconUp,
  DateInputRange,
} from '@ui/src';
import CategoryFilter from './category-filters';
import { NotificationItemIssueStatus } from '../../hoc/center/types';
import { FiltersTypes } from './types';

import cn from './styles.module.css';

const Filters = ({
  setPanelIsVisible,
  selectedCategories,
  setSelectedCategories,
  selectedGroups,
  setSelectedGroups,
  selectedActions,
  setSelectedActions,
  onCategoryFilterApply,
  onCategoryFilterClear,
  selectedStatuses,
  setSelectedStatuses,
  selectedExecutorId,
  employees,
  setSelectedExecutorId,
  categories,
  groups,
  types,
  selectedCategory,
  setSelectedCategory,
  selectedGroup,
  setSelectedGroup,
  categoryFilterTitle,
  withoutCategoryBackButton,
  onApplyStatusFilter,
  onClearStatusFilter,
  onApplyExecutorFilter,
  onClearExecutorFilter,
  deadlineFilter,
  setDeadlineFilter,
  onClearDeadlineFilter,
  filterFields,
}: FiltersTypes) => {
  const [statusFilterVisible, setStatusFilterVisible] = useState(false);
  const [executorFilterVisible, setExecutorFilterVisible] = useState(false);

  return (
    <div className={cn.notificationCenterFilters}>
      <button
        type="button"
        className={`${cn.filtersButton} ${
          filterFields?.createdAt?.length ||
          filterFields?.responsibleId?.length ||
          filterFields?.category?.length ||
          filterFields?.group?.length ||
          filterFields?.executorId?.length ||
          filterFields?.deadlineFrom ||
          filterFields?.type?.length ||
          filterFields?.status?.length
            ? cn.active
            : ''
        }`}
        onClick={() => setPanelIsVisible(true)}
        aria-label="Filters button"
      >
        <IconFilter width={18} height={18} />
      </button>
      <DateInputRange
        placeholder=""
        values={deadlineFilter}
        onChangeRange={(data) =>
          setDeadlineFilter(data.map((item) => format(item, 'yyyy-MM-dd')))
        }
        className={`${cn.deadlineFilter} ${
          deadlineFilter?.length ? cn.active : ''
        }`}
        title="Deadline"
        onReset={onClearDeadlineFilter}
        withTitle
        withoutTwoPickers
        withoutHidePlaceholder
        withoutCustomSelector
        withValueReset
        withResetClose
      />

      <CategoryFilter
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        selectedGroups={selectedGroups}
        setSelectedGroups={setSelectedGroups}
        selectedActions={selectedActions}
        setSelectedActions={setSelectedActions}
        onApply={onCategoryFilterApply}
        onClear={onCategoryFilterClear}
        categories={categories}
        groups={groups}
        types={types}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        title={categoryFilterTitle}
        withoutCategoryBackButton={withoutCategoryBackButton}
      />
      <div className={cn.categoryFilterWrapper}>
        <ClickOutsideDetector setOptionsListVisible={setStatusFilterVisible}>
          <>
            <button
              type="button"
              className={`${cn.categoryFilterButton} ${
                selectedStatuses?.length ? cn.active : ''
              }`}
              onClick={() => setStatusFilterVisible(!statusFilterVisible)}
            >
              <div className={cn.buttonText}>Status</div>
              <div className={cn.iconWrapper}>
                {statusFilterVisible ? <IconUp /> : <IconDown />}
              </div>
            </button>
            {statusFilterVisible && (
              <div className={cn.statusFilter}>
                {[
                  {
                    name: `${NotificationItemIssueStatus.TODO.charAt(
                      0,
                    )}${NotificationItemIssueStatus.TODO.slice(
                      1,
                    ).toLowerCase()}`,
                    value: NotificationItemIssueStatus.TODO,
                  },
                  {
                    name: NotificationItemIssueStatus.IN_PROGRESS.split('_')
                      .map(
                        (item) =>
                          `${item.charAt(0)}${item.slice(1).toLowerCase()}`,
                      )
                      .join(' '),
                    value: NotificationItemIssueStatus.IN_PROGRESS,
                  },
                  {
                    name: `${NotificationItemIssueStatus.DONE.charAt(
                      0,
                    )}${NotificationItemIssueStatus.DONE.slice(
                      1,
                    ).toLowerCase()}`,
                    value: NotificationItemIssueStatus.DONE,
                  },
                  {
                    name: `${NotificationItemIssueStatus.CANCELED.charAt(
                      0,
                    )}${NotificationItemIssueStatus.CANCELED.slice(
                      1,
                    ).toLowerCase()}`,
                    value: NotificationItemIssueStatus.CANCELED,
                  },
                ].map((item) => (
                  <div className={cn.statusFilterItem} key={item.value}>
                    <Checkbox
                      name={`status-${item.value}`}
                      title={item.name}
                      type="checkbox"
                      checked={selectedStatuses?.includes(item.value as string)}
                      onChange={() => {
                        let nextSelectedStatuses = ([] as string[]).concat(
                          selectedStatuses as string[],
                        );

                        if (
                          nextSelectedStatuses.find(
                            (status) => status === item.value,
                          )
                        ) {
                          nextSelectedStatuses = nextSelectedStatuses.filter(
                            (status) => status !== item.value,
                          );
                        } else {
                          nextSelectedStatuses.push(item.value);
                        }

                        setSelectedStatuses(nextSelectedStatuses);
                      }}
                    />
                  </div>
                ))}
                <div className={cn.statusFilterFooter}>
                  <ButtonPrimary
                    text="Apply"
                    size={ButtonSize.SMALL}
                    className={cn.statusFilterApplyButton}
                    onClick={() => {
                      setStatusFilterVisible(false);
                      onApplyStatusFilter();
                    }}
                  />
                  <ButtonPseudo
                    text="Clear"
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      setStatusFilterVisible(false);
                      onClearStatusFilter();
                    }}
                  />
                </div>
              </div>
            )}
          </>
        </ClickOutsideDetector>
      </div>
      <div className={cn.categoryFilterWrapper}>
        <ClickOutsideDetector setOptionsListVisible={setExecutorFilterVisible}>
          <>
            <button
              type="button"
              className={`${cn.categoryFilterButton} ${
                selectedExecutorId && selectedExecutorId?.length > 0
                  ? cn.active
                  : ''
              }`}
              onClick={() => setExecutorFilterVisible(!executorFilterVisible)}
            >
              <div className={cn.buttonText}>Executor</div>
              <div className={cn.iconWrapper}>
                {executorFilterVisible ? <IconUp /> : <IconDown />}
              </div>
            </button>
            {executorFilterVisible && (
              <div className={`${cn.statusFilter} ${cn.moreWidth}`}>
                <div className={cn.employeesList}>
                  {employees?.map((item) => (
                    <button
                      type="button"
                      key={item.value}
                      className={`${cn.employeeButton} ${
                        selectedExecutorId?.includes(item.value as string)
                          ? cn.active
                          : ''
                      }`}
                      onClick={() => {
                        let nextSelectedExecutors = ([] as string[]).concat(
                          selectedExecutorId as string[],
                        );

                        if (
                          nextSelectedExecutors.find(
                            (executor) => executor === item.value,
                          )
                        ) {
                          nextSelectedExecutors = nextSelectedExecutors?.filter(
                            (executor) => executor !== item.value,
                          );
                        } else {
                          nextSelectedExecutors.push(item.value as string);
                        }

                        setSelectedExecutorId(nextSelectedExecutors);
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>

                <div className={cn.statusFilterFooter}>
                  <ButtonPrimary
                    text="Apply"
                    size={ButtonSize.SMALL}
                    className={cn.statusFilterApplyButton}
                    onClick={() => {
                      onApplyExecutorFilter();
                      setExecutorFilterVisible(false);
                    }}
                  />
                  <ButtonPseudo
                    text="Clear"
                    className="button-white"
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      setExecutorFilterVisible(false);
                      onClearExecutorFilter();
                    }}
                  />
                </div>
              </div>
            )}
          </>
        </ClickOutsideDetector>
      </div>
    </div>
  );
};

export default Filters;
